import { Users } from 'lucide-react';
import type { EventSizeGate } from '../../services/events';
import { tierLabel } from '../../utils/eventTiers';

const RECENT_UPGRADE_MS = 10 * 60 * 1000;

interface EventTierBadgeProps {
  guestCount: number;
  sizeGate: EventSizeGate;
}

function isRecentUpgrade(sizeGate: EventSizeGate): boolean {
  if (!sizeGate.lastTierChangeAt || sizeGate.pendingTierCap !== null) return false;
  const changedAt = new Date(sizeGate.lastTierChangeAt).getTime();
  return !Number.isNaN(changedAt) && Date.now() - changedAt < RECENT_UPGRADE_MS;
}

export function EventTierBadge({ guestCount, sizeGate }: EventTierBadgeProps) {
  const overCap = guestCount > sizeGate.tierCap;
  const upgradePending = sizeGate.gateEnabled && sizeGate.pendingTierCap !== null;
  const recentlyUpgraded = !upgradePending && isRecentUpgrade(sizeGate);

  return (
    <span
      data-testid="event-tier-badge"
      title={tierLabel(sizeGate.tierCap)}
      className={`inline-flex items-center gap-1 max-w-full px-2 py-0.5 rounded-lg sm:rounded-full text-xs font-medium sm:whitespace-nowrap ${overCap
        ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300'
        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
    >
      <Users className="w-3.5 h-3.5" aria-hidden="true" />
      <span>
        {guestCount} / {sizeGate.tierCap} guests
        {upgradePending && ` · Upgrade to ${sizeGate.pendingTierCap} pending`}
        {recentlyUpgraded && <span className="text-green-700 dark:text-green-400">{' · Upgraded'}</span>}
      </span>
    </span>
  );
}
